import React, {
  FunctionComponent,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import cloneDeep from 'lodash.clonedeep';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { connect, ConnectedProps } from 'react-redux';
import { RootState } from 'data/root.reducers';
import { CollectionFunctionGroupFormData } from 'data/collectionFunctionGroup/collectionFunctionGroup.types';
import { adaptToForm } from 'data/collectionFunctionGroup/collectionFunctionGroup.adapter';
import * as collectionFunctionGroupSelectors from 'data/collectionFunctionGroup/collectionFunctionGroup.selectors';
import * as globalSelectors from 'global/global-selectors';
import * as actions from 'data/collectionFunctionGroup/collectionFunctionGroup.actions';
import * as discoverySelectors from 'xcipio/discovery-xcipio-selectors';
import * as authenticationSelectors from 'data/authentication/authentication.selectors';
import * as securityInfoSelectors from 'data/securityInfo/securityInfo.selectors';
import * as securityInfoActions from 'data/securityInfo/securityInfo.actions';
import * as contactSelectors from 'data/contact/contact.selectors';
import * as contactActions from 'data/contact/contact.actions';
import { FormTemplateSection } from 'data/template/template.types';
import FormPanel from 'components/FormPanel';
import FormButtons from 'components/FormButtons';
import CollectionFunctionGroupForm, { CollectionFunctionGroupFormRef } from '../../components/CollectionFunctionGroupForm';
import { collectionFunctionGroupTemplate } from '../../templates/collectionFunctionGroupTemplate';

type RouterParams = {
  history: string
}

const mapState = (state: RootState) => ({
  deliveryFunctionId: globalSelectors.getSelectedDeliveryFunctionId(state),
  selectedCollectionFunctionGroup: collectionFunctionGroupSelectors.getSelectedCollectionFunctionGroup(state),
  isAuditEnabled: discoverySelectors.isDiscoverySettingAuditEnabled(state),
  userId: authenticationSelectors.getAuthenticationUserId(state),
  collectionFunctionGroup: collectionFunctionGroupSelectors.getCollectionFunctionGroup(state),
  securityInfos: securityInfoSelectors.getSecurityInfos(state),
  contacts: contactSelectors.getContacts(state),
});

const mapDispatch = {
  goToCollectionGroupEdit: actions.goToCollectionGroupEdit,
  getCollectionFunctionGroup: actions.getCollectionFunctionGroup,
  goToCollectionFunctionGroupList: actions.goToCollectionFunctionGroupList,
  getSecurityInfos: securityInfoActions.getSecurityInfos,
  getContacts: contactActions.getAllContacts,
};

const connector = connect(mapState, mapDispatch);

type PropsFromRedux = ConnectedProps<typeof connector>;
type PropsFromRouter = RouteComponentProps<RouterParams>

export type Props = PropsFromRedux & PropsFromRouter;

export const CollectionFunctionGroupView: FunctionComponent<Props> = ({
  deliveryFunctionId,
  selectedCollectionFunctionGroup,
  history,
  collectionFunctionGroup,
  securityInfos,
  contacts,
  getCollectionFunctionGroup,
  goToCollectionGroupEdit,
  goToCollectionFunctionGroupList,
  getSecurityInfos,
  getContacts,
}: Props) => {
  const [template, setTemplate] = useState(collectionFunctionGroupTemplate);
  const [formData, setFormData] = useState<Partial<CollectionFunctionGroupFormData>>({});
  const collectionFunctionGroupFormRef = useRef<CollectionFunctionGroupFormRef>(null);
  const { t } = useTranslation();

  useEffect(() => {
    getSecurityInfos(deliveryFunctionId);
  }, [deliveryFunctionId, getSecurityInfos]);

  useEffect(() => {
    getContacts(deliveryFunctionId);
  }, [deliveryFunctionId, getContacts]);

  useEffect(() => {
    const id = selectedCollectionFunctionGroup[0]?.id;
    if (id) {
      getCollectionFunctionGroup(deliveryFunctionId, id);
    }
  }, [deliveryFunctionId, selectedCollectionFunctionGroup, getCollectionFunctionGroup]);

  useEffect(() => {
    if (collectionFunctionGroup != null) {
      setFormData(adaptToForm(collectionFunctionGroup));

      const clonedTemplate = cloneDeep(collectionFunctionGroupTemplate);
      clonedTemplate.formId = Date.now().toString();
      (clonedTemplate.main as FormTemplateSection).fields.name.readOnly = true;
      (clonedTemplate.main as FormTemplateSection).fields.name.disabled = true;
      (clonedTemplate.main as FormTemplateSection).fields.description.readOnly = true;
      (clonedTemplate.main as FormTemplateSection).fields.description.disabled = true;
      (clonedTemplate.main as FormTemplateSection).fields.hi1endpointid.readOnly = true;
      (clonedTemplate.main as FormTemplateSection).fields.hi1endpointid.disabled = true;
      (clonedTemplate.main as FormTemplateSection).fields.hi1notifyuri.readOnly = true;
      (clonedTemplate.main as FormTemplateSection).fields.hi1notifyuri.disabled = true;
      (clonedTemplate.main as FormTemplateSection).fields.hi1notifysecid.readOnly = true;
      (clonedTemplate.main as FormTemplateSection).fields.hi1notifysecid.disabled = true;
      (clonedTemplate.main as FormTemplateSection).fields.hi1notifysecid.options = [
        { label: 'None', value: '' },
        ...securityInfos.map(({ name }) => ({ label: name, value: name })),
      ];
      (clonedTemplate.main as FormTemplateSection).fields.contact.readOnly = true;
      (clonedTemplate.main as FormTemplateSection).fields.contact.disabled = true;
      (clonedTemplate.main as FormTemplateSection).fields.contact.options = [
        { label: 'None', value: '' },
        ...contacts.map(({ id, name }) => ({ label: name, value: id })),
      ];

      setTemplate(clonedTemplate);
    }
  }, [collectionFunctionGroup, securityInfos, contacts]);

  const handleClickCancel = () => goToCollectionFunctionGroupList(history);

  const handleClickEdit = () => goToCollectionGroupEdit(history);

  if (!collectionFunctionGroup) {
    return null;
  }

  const renderFormPanel = (formTitle: string) => {
    return (
      <FormPanel title={formTitle}>
        <FormButtons
          isEditing={false}
          onClickCancel={handleClickCancel}
          onClickClose={handleClickCancel}
          onClickEdit={handleClickEdit}
        />
      </FormPanel>
    );
  };

  return (
    <div data-testid="CollectionFunctionGroupView" className="read-only">
      {renderFormPanel(t('collectionFunctionGroup.view.title'))}
      <CollectionFunctionGroupForm
        data={formData}
        template={template}
        ref={collectionFunctionGroupFormRef}
      />
    </div>
  );
};

export default withRouter(connector(CollectionFunctionGroupView));
